import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "../auth/supabaseClient";
import { migrateLegacyId } from "../economy/characters";

export interface FriendEntry {
  odId: string;
  username: string;
  level: number;
  equippedSkin: string;
  online: boolean;
  versusWins: number;
  versusLosses: number;
  versusElo: number;
}

export interface PendingRequest {
  odId: string;
  username: string;
  level: number;
}

export interface SentRequest {
  odId: string;
  username: string;
  level: number;
}

export class FriendManager {
  private localUserId: string | null = null;
  private presenceChannel: RealtimeChannel | null = null;
  private onlineUserIds = new Set<string>();
  private friends: FriendEntry[] = [];
  private pendingRequests: PendingRequest[] = [];
  private sentRequests: SentRequest[] = [];
  private processingRequestIds = new Set<string>();

  init(userId: string, appearOffline = false): void {
    this.localUserId = userId;
    if (!appearOffline) {
      this.joinPresence(userId);
    }
    // Initial fetch so data is available before opening FRIENDS page
    void this.refreshAll();
  }

  /** Re-fetch friends, pending, and sent from Supabase. */
  async refreshAll(): Promise<void> {
    await Promise.all([
      this.fetchFriends(),
      this.fetchPendingRequests(),
      this.fetchSentRequests(),
    ]);
  }

  setAppearOffline(offline: boolean): void {
    if (offline) {
      this.leavePresence();
    } else if (this.localUserId != null && this.presenceChannel == null) {
      this.joinPresence(this.localUserId);
    }
  }

  destroy(): void {
    this.leavePresence();
    this.localUserId = null;
    this.friends = [];
    this.pendingRequests = [];
    this.sentRequests = [];
    this.processingRequestIds.clear();
  }

  // ── Presence ─────────────────────────────────────────────────────────────

  private joinPresence(userId: string): void {
    this.leavePresence();
    const supabase = getSupabase();
    const channel = supabase.channel("bb:presence", {
      config: { presence: { key: userId } },
    });

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState<{ userId: string }>();
      this.onlineUserIds.clear();
      for (const presences of Object.values(state)) {
        for (const p of presences) {
          this.onlineUserIds.add(p.userId);
        }
      }
    });

    void channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel.track({ userId });
      }
    });

    this.presenceChannel = channel;
  }

  private leavePresence(): void {
    if (this.presenceChannel != null) {
      void this.presenceChannel.unsubscribe();
      const supabase = getSupabase();
      void supabase.removeChannel(this.presenceChannel);
      this.presenceChannel = null;
    }
    this.onlineUserIds.clear();
  }

  // ── Friends CRUD ─────────────────────────────────────────────────────────

  async sendRequest(username: string): Promise<{ error: string | null }> {
    if (this.localUserId == null) return { error: "NOT SIGNED IN" };
    const trimmed = username.trim().toLowerCase();
    if (!trimmed) return { error: "ENTER A USERNAME" };

    const supabase = getSupabase();

    // Look up user by username only
    const { data: target, error: lookupErr } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", trimmed)
      .single();

    if (lookupErr || target == null) {
      return { error: "USER NOT FOUND" };
    }
    if (target.id === this.localUserId) {
      return { error: "CANNOT ADD YOURSELF" };
    }

    const { error: insertErr } = await supabase.from("friends").insert({
      user_id: this.localUserId,
      friend_id: target.id,
      status: "pending",
    });

    if (insertErr) {
      if (insertErr.code === "23505") return { error: "ALREADY SENT" };
      console.warn("[friends] sendRequest failed:", insertErr.message);
      return { error: "REQUEST FAILED" };
    }
    return { error: null };
  }

  isRequestProcessing(friendUserId: string): boolean {
    return this.processingRequestIds.has(friendUserId);
  }

  async acceptRequest(friendUserId: string): Promise<void> {
    if (this.localUserId == null) return;
    if (this.processingRequestIds.has(friendUserId)) return;
    this.processingRequestIds.add(friendUserId);
    try {
      const supabase = getSupabase();
      await supabase
        .from("friends")
        .update({ status: "accepted" })
        .eq("user_id", friendUserId)
        .eq("friend_id", this.localUserId);
    } finally {
      this.processingRequestIds.delete(friendUserId);
    }
  }

  async declineRequest(friendUserId: string): Promise<void> {
    if (this.localUserId == null) return;
    if (this.processingRequestIds.has(friendUserId)) return;
    this.processingRequestIds.add(friendUserId);
    try {
      const supabase = getSupabase();
      await supabase
        .from("friends")
        .delete()
        .eq("user_id", friendUserId)
        .eq("friend_id", this.localUserId);
    } finally {
      this.processingRequestIds.delete(friendUserId);
    }
  }

  async removeFriend(friendUserId: string): Promise<void> {
    if (this.localUserId == null) return;
    const supabase = getSupabase();
    // Delete both directions
    await supabase
      .from("friends")
      .delete()
      .or(`and(user_id.eq.${this.localUserId},friend_id.eq.${friendUserId}),and(user_id.eq.${friendUserId},friend_id.eq.${this.localUserId})`);
  }

  async fetchFriends(): Promise<FriendEntry[]> {
    if (this.localUserId == null) return [];
    const supabase = getSupabase();

    // Friends where we sent or received accepted request
    const { data, error } = await supabase
      .from("friends")
      .select("user_id, friend_id, profiles!friends_friend_id_fkey(id, username, level, equipped_character, versus_wins, versus_losses, versus_elo), sender:profiles!friends_user_id_fkey(id, username, level, equipped_character, versus_wins, versus_losses, versus_elo)")
      .or(`user_id.eq.${this.localUserId},friend_id.eq.${this.localUserId}`)
      .eq("status", "accepted");

    if (error || data == null) {
      console.warn("[friends] fetchFriends failed:", error?.message);
      return [];
    }

    const mapped = data.map((row: Record<string, unknown>) => {
      const isSender = (row as { user_id: string }).user_id === this.localUserId;
      type FriendProfile = { id: string; username: string | null; level: number; equipped_character: string | null; versus_wins: number | null; versus_losses: number | null; versus_elo: number | null };
      const friendProfile = isSender
        ? (row as { profiles: FriendProfile }).profiles
        : (row as { sender: FriendProfile }).sender;
      return {
        odId: friendProfile.id,
        username: friendProfile.username ?? "???",
        level: friendProfile.level ?? 1,
        equippedSkin: migrateLegacyId(friendProfile.equipped_character ?? "skin_001"),
        online: this.onlineUserIds.has(friendProfile.id),
        versusWins: friendProfile.versus_wins ?? 0,
        versusLosses: friendProfile.versus_losses ?? 0,
        versusElo: friendProfile.versus_elo ?? 0,
      };
    });
    // Deduplicate by user ID — keep the first entry per friend
    const seen = new Set<string>();
    this.friends = mapped.filter(f => {
      if (seen.has(f.odId)) return false;
      seen.add(f.odId);
      return true;
    });
    return this.friends;
  }

  async fetchPendingRequests(): Promise<PendingRequest[]> {
    if (this.localUserId == null) return [];
    const supabase = getSupabase();

    const { data, error } = await supabase
      .from("friends")
      .select("user_id, profiles!friends_user_id_fkey(id, username, level)")
      .eq("friend_id", this.localUserId)
      .eq("status", "pending");

    if (error || data == null) {
      console.warn("[friends] fetchPendingRequests failed:", error?.message);
      return [];
    }

    this.pendingRequests = data.map((row: Record<string, unknown>) => {
      const sender = (row as { profiles: { id: string; username: string | null; level: number } }).profiles;
      return {
        odId: sender.id,
        username: sender.username ?? "???",
        level: sender.level ?? 1,
      };
    });
    return this.pendingRequests;
  }

  async fetchSentRequests(): Promise<SentRequest[]> {
    if (this.localUserId == null) return [];
    const supabase = getSupabase();

    const { data, error } = await supabase
      .from("friends")
      .select("friend_id, profiles!friends_friend_id_fkey(id, username, level)")
      .eq("user_id", this.localUserId)
      .eq("status", "pending");

    if (error || data == null) {
      console.warn("[friends] fetchSentRequests failed:", error?.message);
      return [];
    }

    this.sentRequests = data.map((row: Record<string, unknown>) => {
      const target = (row as { profiles: { id: string; username: string | null; level: number } }).profiles;
      return {
        odId: target.id,
        username: target.username ?? "???",
        level: target.level ?? 1,
      };
    });
    return this.sentRequests;
  }

  getCachedFriends(): FriendEntry[] {
    // Update online status from presence
    for (const f of this.friends) {
      f.online = this.onlineUserIds.has(f.odId);
    }
    return this.friends;
  }

  getCachedPendingRequests(): PendingRequest[] {
    return this.pendingRequests;
  }

  getCachedSentRequests(): SentRequest[] {
    return this.sentRequests;
  }

  getOnlineFriendCount(): number {
    return this.friends.filter((f) => this.onlineUserIds.has(f.odId)).length;
  }
}
